// src/index.ts
import { z as z2 } from "zod";

// src/events.ts
var Events = {
  System: {
    SYNC: "SYSTEM.SYNC",
    INIT: "SYSTEM.INIT",
    PLAYER_JOINED: "SYSTEM.PLAYER_JOINED",
    PLAYER_CONNECTED: "SYSTEM.PLAYER_CONNECTED",
    WAKEUP: "SYSTEM.WAKEUP",
    PAUSE: "SYSTEM.PAUSE"
  },
  Admin: {
    NEXT_STAGE: "ADMIN.NEXT_STAGE",
    INJECT_TIMELINE_EVENT: "ADMIN.INJECT_TIMELINE_EVENT",
    UPDATE_PUSH_CONFIG: "ADMIN.UPDATE_PUSH_CONFIG"
  },
  Social: {
    PREFIX: "SOCIAL.",
    SEND_MSG: "SOCIAL.SEND_MSG",
    SEND_SILVER: "SOCIAL.SEND_SILVER",
    USE_PERK: "SOCIAL.USE_PERK",
    CREATE_CHANNEL: "SOCIAL.CREATE_CHANNEL",
    ADD_MEMBER: "SOCIAL.ADD_MEMBER",
    ACCEPT_DM: "SOCIAL.ACCEPT_DM",
    DECLINE_DM: "SOCIAL.DECLINE_DM",
    REACT: "SOCIAL.REACT",
    NUDGE: "SOCIAL.NUDGE",
    WHISPER: "SOCIAL.WHISPER"
  },
  Confession: {
    PREFIX: "CONFESSION.",
    POST: "CONFESSION.POST"
  },
  Pregame: {
    PREFIX: "PREGAME.",
    REVEAL_ANSWER: "PREGAME.REVEAL_ANSWER"
  },
  Internal: {
    READY: "INTERNAL.READY",
    END_DAY: "INTERNAL.END_DAY",
    START_CARTRIDGE: "INTERNAL.START_CARTRIDGE",
    OPEN_VOTING: "INTERNAL.OPEN_VOTING",
    CLOSE_VOTING: "INTERNAL.CLOSE_VOTING",
    OPEN_DMS: "INTERNAL.OPEN_DMS",
    CLOSE_DMS: "INTERNAL.CLOSE_DMS",
    OPEN_GROUP_CHAT: "INTERNAL.OPEN_GROUP_CHAT",
    CLOSE_GROUP_CHAT: "INTERNAL.CLOSE_GROUP_CHAT",
    INJECT_PROMPT: "INTERNAL.INJECT_PROMPT",
    START_GAME: "INTERNAL.START_GAME",
    END_GAME: "INTERNAL.END_GAME",
    START_ACTIVITY: "INTERNAL.START_ACTIVITY",
    END_ACTIVITY: "INTERNAL.END_ACTIVITY",
    START_DILEMMA: "INTERNAL.START_DILEMMA",
    END_DILEMMA: "INTERNAL.END_DILEMMA",
    START_CONFESSION_CHAT: "INTERNAL.START_CONFESSION_CHAT",
    END_CONFESSION_CHAT: "INTERNAL.END_CONFESSION_CHAT"
  },
  Cartridge: {
    VOTE_RESULT: "CARTRIDGE.VOTE_RESULT",
    GAME_RESULT: "CARTRIDGE.GAME_RESULT",
    PLAYER_GAME_RESULT: "CARTRIDGE.PLAYER_GAME_RESULT",
    PROMPT_RESULT: "CARTRIDGE.PROMPT_RESULT",
    DILEMMA_RESULT: "CARTRIDGE.DILEMMA_RESULT"
  },
  Fact: { RECORD: "FACT.RECORD" },
  Presence: {
    UPDATE: "PRESENCE.UPDATE",
    TYPING: "PRESENCE.TYPING",
    STOP_TYPING: "PRESENCE.STOP_TYPING"
  },
  Ticker: {
    UPDATE: "TICKER.UPDATE",
    HISTORY: "TICKER.HISTORY",
    DEBUG: "TICKER.DEBUG"
  },
  Rejection: {
    DM: "DM.REJECTED",
    SILVER_TRANSFER: "SILVER_TRANSFER.REJECTED",
    CHANNEL: "CHANNEL.REJECTED",
    PERK: "PERK.REJECTED"
  },
  Perk: { RESULT: "PERK.RESULT" },
  GameChannel: {
    CREATE: "GAME.CHANNEL.CREATE",
    DESTROY: "GAME.CHANNEL.DESTROY"
  },
  // Dynamic event helpers + namespace prefixes
  Vote: {
    PREFIX: "VOTE.",
    event: (mechanism, action) => `VOTE.${mechanism}.${action}`
  },
  Game: {
    PREFIX: "GAME.",
    RETRY: "GAME.RETRY",
    SUBMIT: "GAME.SUBMIT",
    CHANNEL_PREFIX: "GAME.CHANNEL.",
    start: (gameType) => `GAME.${gameType}.START`,
    result: (gameType) => `GAME.${gameType}.RESULT`,
    event: (gameType, action) => `GAME.${gameType}.${action}`
  },
  Activity: {
    PREFIX: "ACTIVITY.",
    event: (promptType, action) => `ACTIVITY.${promptType}.${action}`
  },
  Dilemma: {
    PREFIX: "DILEMMA.",
    submit: (dilemmaType) => `DILEMMA.${dilemmaType}.SUBMIT`
  },
  Economy: {
    PREFIX: "ECONOMY.",
    CREDIT_SILVER: "ECONOMY.CREDIT_SILVER",
    CONTRIBUTE_GOLD: "ECONOMY.CONTRIBUTE_GOLD"
  },
  GameMaster: {
    PREFIX: "GAME_MASTER.",
    RESOLVE_DAY: "GAME_MASTER.RESOLVE_DAY",
    DAY_ENDED: "GAME_MASTER.DAY_ENDED",
    GAME_ENDED: "GAME_MASTER.GAME_ENDED"
  }
};
var FactTypes = {
  CHAT_MSG: "CHAT_MSG",
  SILVER_TRANSFER: "SILVER_TRANSFER",
  VOTE_CAST: "VOTE_CAST",
  ELIMINATION: "ELIMINATION",
  DM_SENT: "DM_SENT",
  POWER_USED: "POWER_USED",
  PERK_USED: "PERK_USED",
  GAME_RESULT: "GAME_RESULT",
  PLAYER_GAME_RESULT: "PLAYER_GAME_RESULT",
  WINNER_DECLARED: "WINNER_DECLARED",
  PROMPT_RESULT: "PROMPT_RESULT",
  GAME_ROUND: "GAME_ROUND",
  GAME_DECISION: "GAME_DECISION",
  ALL_SUBMITTED: "ALL_SUBMITTED",
  DM_INVITE_SENT: "DM_INVITE_SENT",
  DM_INVITE_ACCEPTED: "DM_INVITE_ACCEPTED",
  DM_INVITE_DECLINED: "DM_INVITE_DECLINED",
  DILEMMA_RESULT: "DILEMMA_RESULT",
  REACTION: "REACTION",
  NUDGE: "NUDGE",
  WHISPER: "WHISPER",
  CONFESSION_POSTED: "CONFESSION_POSTED",
  CONFESSION_PHASE_STARTED: "CONFESSION_PHASE_STARTED",
  CONFESSION_PHASE_ENDED: "CONFESSION_PHASE_ENDED",
  PREGAME_PLAYER_JOINED: "PREGAME_PLAYER_JOINED",
  PREGAME_REVEAL_ANSWER: "PREGAME_REVEAL_ANSWER"
};
var VotingPhases = {
  EXPLAIN: "EXPLAIN",
  VOTING: "VOTING",
  REVEAL: "REVEAL",
  EXECUTIONER_PICKING: "EXECUTIONER_PICKING",
  WINNER: "WINNER"
};
var ArcadePhases = {
  NOT_STARTED: "NOT_STARTED",
  PLAYING: "PLAYING",
  AWAITING_DECISION: "AWAITING_DECISION",
  COMPLETED: "COMPLETED"
};
var PromptPhases = {
  ACTIVE: "ACTIVE",
  RESULTS: "RESULTS",
  COLLECTING: "COLLECTING",
  VOTING: "VOTING",
  ANSWERING: "ANSWERING",
  GUESSING: "GUESSING"
};
var SyncDecisionPhases = {
  COLLECTING: "COLLECTING",
  ROUND_REVEAL: "ROUND_REVEAL",
  REVEAL: "REVEAL"
};
var LiveGamePhases = {
  INIT: "INIT",
  WAITING_FOR_START: "WAITING_FOR_START",
  READY: "READY",
  COUNTDOWN: "COUNTDOWN",
  ACTIVE: "ACTIVE",
  COMPLETED: "COMPLETED"
};
var RealtimeTriviaPhases = {
  WAITING: "WAITING",
  QUESTION: "QUESTION",
  RESULT: "RESULT",
  SCOREBOARD: "SCOREBOARD"
};
var PlayerStatuses = {
  ALIVE: "ALIVE",
  ELIMINATED: "ELIMINATED"
};
var TickerCategories = {
  PHASE_DAY_START: "PHASE.DAY_START",
  PHASE_NIGHT: "PHASE.NIGHT",
  PHASE_GAME_OVER: "PHASE.GAME_OVER",
  PHASE_WINNER: "PHASE.WINNER",
  GATE_CHAT_OPEN: "GATE.CHAT_OPEN",
  GATE_CHAT_CLOSE: "GATE.CHAT_CLOSE",
  GATE_DMS_OPEN: "GATE.DMS_OPEN",
  GATE_DMS_CLOSE: "GATE.DMS_CLOSE",
  VOTE: "VOTE",
  GAME: "GAME",
  GAME_REWARD: "GAME.REWARD",
  SOCIAL_TRANSFER: "SOCIAL.TRANSFER",
  SOCIAL_PERK: "SOCIAL.PERK",
  SOCIAL_NUDGE: "SOCIAL.NUDGE",
  SOCIAL_WHISPER: "SOCIAL.WHISPER",
  SOCIAL_INVITE: "SOCIAL.INVITE",
  SOCIAL_PHASE: "SOCIAL.PHASE",
  ACTIVITY: "ACTIVITY",
  ELIMINATION: "ELIMINATION",
  GOLD_POOL: "GOLD.POOL",
  DILEMMA: "DILEMMA"
};
var VoteEvents = {
  MAJORITY: { CAST: "VOTE.MAJORITY.CAST" },
  EXECUTIONER: { ELECT: "VOTE.EXECUTIONER.ELECT", PICK: "VOTE.EXECUTIONER.PICK" },
  BUBBLE: { CAST: "VOTE.BUBBLE.CAST" },
  PODIUM_SACRIFICE: { CAST: "VOTE.PODIUM_SACRIFICE.CAST" },
  SECOND_TO_LAST: {},
  // no client events (display-only)
  SHIELD: { SAVE: "VOTE.SHIELD.SAVE" },
  TRUST_PAIRS: { TRUST: "VOTE.TRUST_PAIRS.TRUST", ELIMINATE: "VOTE.TRUST_PAIRS.ELIMINATE" },
  FINALS: { CAST: "VOTE.FINALS.CAST" }
};
var ActivityEvents = {
  PROMPT: { SUBMIT: "ACTIVITY.PROMPT.SUBMIT" },
  WYR: { CHOOSE: "ACTIVITY.WYR.CHOOSE" },
  HOTTAKE: { RESPOND: "ACTIVITY.HOTTAKE.RESPOND" },
  CONFESSION: { SUBMIT: "ACTIVITY.CONFESSION.SUBMIT", VOTE: "ACTIVITY.CONFESSION.VOTE" },
  GUESSWHO: { ANSWER: "ACTIVITY.GUESSWHO.ANSWER", GUESS: "ACTIVITY.GUESSWHO.GUESS" }
};
var DilemmaPhases = {
  ANNOUNCED: "ANNOUNCED",
  COLLECTING: "COLLECTING",
  REVEAL: "REVEAL"
};
var DilemmaEvents = {
  SILVER_GAMBIT: { SUBMIT: "DILEMMA.SILVER_GAMBIT.SUBMIT" },
  SPOTLIGHT: { SUBMIT: "DILEMMA.SPOTLIGHT.SUBMIT" },
  GIFT_OR_GRIEF: { SUBMIT: "DILEMMA.GIFT_OR_GRIEF.SUBMIT" }
};
var TriviaEvents = {
  START: "GAME.TRIVIA.START",
  ANSWER: "GAME.TRIVIA.ANSWER"
};
var RealtimeTriviaEvents = {
  ANSWER: "GAME.REALTIME_TRIVIA.ANSWER"
};
var TouchScreenEvents = {
  START: "GAME.TOUCH_SCREEN.START",
  READY: "GAME.TOUCH_SCREEN.READY",
  TOUCH: "GAME.TOUCH_SCREEN.TOUCH",
  RELEASE: "GAME.TOUCH_SCREEN.RELEASE"
};
var ALLOWED_CLIENT_EVENTS = [
  Events.Social.SEND_MSG,
  Events.Social.SEND_SILVER,
  Events.Social.USE_PERK,
  Events.Social.CREATE_CHANNEL,
  Events.Social.ADD_MEMBER,
  Events.Social.ACCEPT_DM,
  Events.Social.DECLINE_DM,
  Events.Social.REACT,
  Events.Social.NUDGE,
  Events.Social.WHISPER,
  Events.Pregame.REVEAL_ANSWER
];

// src/config.ts
var Config = {
  dm: {
    maxPartnersPerDay: 3,
    maxCharsPerDay: 1200,
    silverCost: 1,
    maxGroupsPerDay: 3
  },
  chat: {
    maxLogSize: 500,
    maxMessageLength: 280
  },
  confession: {
    maxConfessionLength: 280
    // chosen over Config.chat.maxMessageLength to force brevity + Twitter-feel cadence
  },
  perk: {
    costs: { SPY_DMS: 5, EXTRA_DM_PARTNER: 3, EXTRA_DM_CHARS: 2 },
    extraCharsBonus: 600
  },
  prompt: {
    silverParticipation: 5,
    silverMutualBonus: 10,
    silverConsensusBonus: 10,
    silverMinorityBonus: 10,
    confession: {
      silverSubmit: 5,
      silverVote: 5,
      silverWinner: 15
    },
    guessWho: {
      silverPerCorrectGuess: 5,
      silverPerFooled: 5
    }
  },
  game: {
    arcade: {
      maxSilver: 15,
      defaultTimeLimitMs: 45e3,
      difficultyScalePerDay: 0.15
    },
    gapRun: {
      timeLimitMs: 45e3,
      maxDistanceSilver: 15,
      survivalBonus: 5,
      distancePerSilver: 100,
      survivalGraceMs: 1e3,
      distancePerGold: 500
    },
    gridPush: {
      timeLimitMs: 18e4,
      scorePerSilver: 5,
      scorePerGold: 25
    },
    colorMatch: {
      timeLimitMs: 6e4,
      correctPerGold: 5
    },
    reactionTime: {
      timeLimitMs: 6e4,
      roundsPerGold: 3
    },
    sequence: {
      timeLimitMs: 18e4,
      silverPerRound: 2,
      roundsPerGold: 3
    },
    simonSays: {
      timeLimitMs: 18e4,
      silverPerRound: 2,
      roundsPerGold: 3
    },
    quickMath: {
      timeLimitMs: 9e4,
      streakPerBonus: 3,
      correctPerGold: 4
    },
    aimTrainer: {
      timeLimitMs: 6e4,
      scorePerSilver: 10,
      scorePerGold: 50
    },
    stacker: {
      timeLimitMs: 12e4,
      layersPerGold: 3
    },
    trivia: {
      totalRounds: 5,
      questionTimeMs: 15e3,
      baseSilver: 2,
      maxSpeedBonus: 3,
      perfectBonus: 5,
      goldPerCorrect: 1,
      questionPoolSize: 50,
      networkGraceMs: 1e3,
      resultDisplayMs: 3e3
    },
    touchScreen: {
      readyTimeoutMs: 15e3,
      countdownMs: 3e3,
      maxHoldTimeMs: 3e5,
      silverByRank: [15, 10, 7, 3],
      msPerGoldUnit: 5e3
    },
    blindAuction: {
      prizePool: [5, 10, 15, 20],
      prizeSlots: 3
    },
    betBetBet: {
      tiePenaltyMultiplier: 5
    },
    kingsRansom: {
      vaultMinimum: 10,
      vaultFraction: 0.3
    },
    theSplit: {
      basePot: 5,
      potIncrement: 5,
      revealDurationMs: 8e3,
      winnerBonus: 10
    },
    shockwave: {
      timeLimitMs: 6e4,
      scorePerSilver: 3,
      nearMissBonus: 5,
      scorePerGold: 15
    },
    orbit: {
      timeLimitMs: 6e4,
      transfersPerSilver: 2,
      perfectsPerBonusSilver: 3,
      transfersPerGold: 10
    },
    beatDrop: {
      timeLimitMs: 9e4,
      scorePerSilver: 500,
      perfectAccuracyBonus: 3,
      scorePerGold: 2500,
      startBpm: 80,
      endBpm: 140,
      maxLives: 3
    },
    inflate: {
      timeLimitMs: 45e3,
      maxLives: 3,
      scorePerSilver: 250,
      perfectBankBonus: 2,
      scorePerGold: 1200
    },
    snake: {
      timeLimitMs: 45e3,
      gridSize: 15,
      scorePerSilver: 4,
      lengthBonus: 3,
      scorePerGold: 20
    },
    flappy: {
      timeLimitMs: 6e4,
      scorePerSilver: 3,
      coinBonus: 5,
      scorePerGold: 15
    },
    colorSort: {
      timeLimitMs: 12e4,
      tubeCapacity: 4,
      colorsCount: 5,
      emptyTubes: 2,
      scorePerSilver: 20,
      solvedBonus: 5,
      scorePerGold: 100
    },
    blink: {
      timeLimitMs: 3e4,
      scorePerSilver: 10,
      scorePerGold: 50,
      whitePenalty: 3,
      minStateMs: 220
    },
    recall: {
      timeLimitMs: 9e4,
      startSize: 3,
      maxSize: 6,
      // Silver awarded cumulatively: 3×3=1, 4×4=2, 5×5=4, 6×6=8 → 15 total if cleared
      silverBySize: [0, 0, 0, 1, 2, 4, 8],
      fullClearGold: 1
    }
  },
  dilemma: {
    silverParticipation: 5,
    silverGambit: {
      donationCost: 5,
      jackpotMultiplier: 3
    },
    spotlight: {
      unanimousReward: 20,
      participationReward: 5
    },
    giftOrGrief: {
      giftAmount: 10,
      griefAmount: 10
    }
  }
};

// src/cycle-defaults.ts
var VOTE_POOL = [
  { type: "BUBBLE", minPlayers: 6 },
  { type: "TRUST_PAIRS", minPlayers: 5 },
  { type: "PODIUM_SACRIFICE", minPlayers: 5 },
  { type: "EXECUTIONER", minPlayers: 5 },
  { type: "SHIELD", minPlayers: 4 },
  { type: "SECOND_TO_LAST", minPlayers: 3 },
  { type: "MAJORITY", minPlayers: 3 }
];
var GAME_POOL = [
  { type: "TRIVIA", minPlayers: 2 },
  { type: "THE_SPLIT", minPlayers: 4 },
  { type: "GRID_PUSH", minPlayers: 2 },
  { type: "KINGS_RANSOM", minPlayers: 4 },
  { type: "BET_BET_BET", minPlayers: 4 },
  { type: "REALTIME_TRIVIA", minPlayers: 2 },
  { type: "BLIND_AUCTION", minPlayers: 4 },
  { type: "SEQUENCE", minPlayers: 2 },
  { type: "TOUCH_SCREEN", minPlayers: 3 },
  { type: "REACTION_TIME", minPlayers: 2 },
  { type: "COLOR_MATCH", minPlayers: 2 },
  { type: "QUICK_MATH", minPlayers: 2 },
  { type: "STACKER", minPlayers: 2 },
  { type: "GAP_RUN", minPlayers: 2 },
  { type: "SIMON_SAYS", minPlayers: 2 },
  { type: "AIM_TRAINER", minPlayers: 2 },
  { type: "SHOCKWAVE", minPlayers: 2 },
  { type: "ORBIT", minPlayers: 2 },
  { type: "BEAT_DROP", minPlayers: 2 },
  { type: "INFLATE", minPlayers: 2 },
  { type: "SNAKE", minPlayers: 2 },
  { type: "FLAPPY", minPlayers: 2 },
  { type: "COLOR_SORT", minPlayers: 2 },
  { type: "BLINK", minPlayers: 2 },
  { type: "RECALL", minPlayers: 2 }
];
var ACTIVITY_POOL = [
  { type: "PLAYER_PICK", minPlayers: 3 },
  { type: "PREDICTION", minPlayers: 3 },
  { type: "WOULD_YOU_RATHER", minPlayers: 3 },
  { type: "CONFESSION", minPlayers: 3 },
  { type: "HOT_TAKE", minPlayers: 3 },
  { type: "GUESS_WHO", minPlayers: 3 }
];
var LIVE_GAMES = /* @__PURE__ */ new Set(["TOUCH_SCREEN"]);
function pickNext(pool, used, playerCount) {
  return pool.find((m) => !used.has(m.type) && playerCount >= m.minPlayers)?.type;
}
function generateCycleDefaults(dayCount) {
  const usedVotes = /* @__PURE__ */ new Set();
  const usedGames = /* @__PURE__ */ new Set();
  const usedActivities = /* @__PURE__ */ new Set();
  return Array.from({ length: dayCount }, (_, i) => {
    const playerCount = dayCount + 1 - i;
    const isLastDay = i === dayCount - 1;
    const voteType = isLastDay ? "FINALS" : pickNext(VOTE_POOL, usedVotes, playerCount) ?? "MAJORITY";
    usedVotes.add(voteType);
    const gameType = pickNext(GAME_POOL, usedGames, playerCount) ?? "TRIVIA";
    usedGames.add(gameType);
    const activityType = isLastDay ? "NONE" : pickNext(ACTIVITY_POOL, usedActivities, playerCount) ?? "NONE";
    usedActivities.add(activityType);
    return {
      voteType,
      gameType,
      ...LIVE_GAMES.has(gameType) && { gameMode: "LIVE" },
      activityType
    };
  });
}
function isLiveGame(gameType) {
  return LIVE_GAMES.has(gameType);
}

// src/economy-info.ts
var ECONOMY_INFO = {
  silver: {
    name: "Silver",
    description: "Personal currency earned from mini-games and activities. Spend it on DMs, perks, and silver transfers to other players.",
    earnedFrom: [
      "Mini-games (score-based)",
      "Activities/prompts",
      "Silver transfers from other players"
    ],
    spentOn: [
      "Sending DMs (per message)",
      "Buying perks (SPY_DMS, EXTRA_DM_PARTNER, EXTRA_DM_CHARS)",
      "Silver transfers to other players"
    ]
  },
  gold: {
    name: "Gold",
    description: "Group pool contributed from mini-game performance. The gold pool is split among finalists at game end.",
    earnedFrom: ["Mini-game gold contributions (automatic)"],
    spentOn: ["Distributed to winner/finalists at game end"]
  }
};

// src/vote-type-info.ts
var VOTE_TYPE_INFO = {
  MAJORITY: {
    name: "Majority",
    description: "The most-voted player is eliminated.",
    howItWorks: "Everyone picks one player. The most-picked is out. Tie? Lowest silver loses.",
    header: "Majority: Eliminate a Player",
    cta: "Who should go?",
    oneLiner: "Most votes out \xB7 Ties: lowest silver",
    moodSubtitle: "Most votes go home",
    revealLabel: "Out",
    confirmTemplate: "Eliminate {name}?",
    actionVerb: "voted for",
    eliminatedSubtitle: "Voted out by the group",
    noEliminationCopy: "The group spared everyone",
    mechanismTone: "punitive",
    minPlayers: 3,
    selectableInLobby: true
  },
  EXECUTIONER: {
    name: "Executioner",
    description: "Elect one player \u2014 they decide who goes.",
    howItWorks: "Step 1: vote for an executioner. Step 2: they pick who\u2019s out. One hand, one cut.",
    header: "Executioner: Elect a Judge",
    cta: "Who gets the power?",
    oneLiner: "One hand picks \xB7 One player falls",
    moodSubtitle: "One hand makes the cut",
    revealLabel: "Cut",
    confirmTemplate: "Elect {name}?",
    actionVerb: "elected",
    eliminatedSubtitle: "Cut down by the executioner",
    noEliminationCopy: "The executioner stayed their hand",
    mechanismTone: "punitive",
    minPlayers: 5,
    selectableInLobby: true,
    executionerPickHeader: "Executioner: Pick Your Target",
    executionerPickCta: "You\u2019re the executioner. Who goes?",
    executionerPickConfirm: "Eliminate {name}?",
    executionerPickVerb: "eliminated",
    executionerRevealCaption: "The executioner"
  },
  BUBBLE: {
    name: "Bubble",
    description: "Saves keep you floating. Fewest saves sinks.",
    howItWorks: "Pick one player to save. The fewest-saved player is out. Top 3 silver holders are immune.",
    header: "Bubble: Save a Player",
    cta: "Who do you save?",
    oneLiner: "Fewest saves out \xB7 Top 3 silver immune",
    moodSubtitle: "Saves keep you afloat",
    revealLabel: "Sunk",
    confirmTemplate: "Save {name}?",
    actionVerb: "saved",
    eliminatedSubtitle: "Not enough saves to stay afloat",
    noEliminationCopy: "Everyone floated through",
    mechanismTone: "punitive",
    minPlayers: 6,
    selectableInLobby: true
  },
  PODIUM_SACRIFICE: {
    name: "Podium Sacrifice",
    description: "The richest are on the block \u2014 the group saves one.",
    howItWorks: "The 3 richest players are on the podium. Everyone else saves one of them. The unsaved one falls.",
    header: "Podium: Save One from the Top",
    cta: "Which top player stays?",
    oneLiner: "Top 3 at risk \xB7 Save one, one falls",
    moodSubtitle: "The top bleeds first",
    revealLabel: "Fallen",
    confirmTemplate: "Save {name}?",
    actionVerb: "saved",
    eliminatedSubtitle: "Sacrificed from the top",
    noEliminationCopy: "The podium stands",
    mechanismTone: "punitive",
    minPlayers: 5,
    selectableInLobby: true
  },
  SECOND_TO_LAST: {
    name: "Second to Last",
    description: "No vote. The second-poorest player is out.",
    howItWorks: "No voting. The player with the second-lowest silver is automatically out. Fate, not choice.",
    header: "Second to Last",
    cta: "",
    oneLiner: "Second-lowest silver is out",
    moodSubtitle: "Fate, not choice",
    revealLabel: "Last",
    confirmTemplate: "",
    actionVerb: "",
    eliminatedSubtitle: "Second-lowest in silver",
    noEliminationCopy: "No one to cut",
    mechanismTone: "fate",
    minPlayers: 3,
    selectableInLobby: true
  },
  SHIELD: {
    name: "Shield",
    description: "Protect first. The group eliminates after.",
    howItWorks: "Pick one player to shield. The most-shielded is safe. Then the group votes someone else out.",
    header: "Shield: Protect a Player",
    cta: "Who do you shield?",
    oneLiner: "Most-shielded safe \xB7 Group eliminates next",
    moodSubtitle: "Saved first \u2014 then sorted",
    revealLabel: "Unshielded",
    confirmTemplate: "Shield {name}?",
    actionVerb: "shielded",
    eliminatedSubtitle: "No shield, no save",
    noEliminationCopy: "Shields held all around",
    mechanismTone: "punitive",
    minPlayers: 4,
    selectableInLobby: true
  },
  TRUST_PAIRS: {
    name: "Trust Pairs",
    description: "Paired. Trust or betray. Only one of you can be wrong.",
    howItWorks: "You\u2019re paired with one other player. You each secretly pick TRUST or BETRAY. Both trust = both safe. One betrays = the truster is out. Both betray = both survive.",
    header: "Trust Pairs: Trust or Betray",
    cta: "Do you trust your partner?",
    oneLiner: "Both trust = safe \xB7 One betrays = truster out",
    moodSubtitle: "Trust, or burn it",
    revealLabel: "Betrayed",
    confirmTemplate: "Trust {name}?",
    actionVerb: "trusted",
    eliminatedSubtitle: "Betrayed by a partner",
    noEliminationCopy: "No betrayal landed",
    mechanismTone: "punitive",
    minPlayers: 5,
    selectableInLobby: true
  },
  DUELS: {
    name: "Duels",
    description: "Paired for head-to-head. The group picks the survivor.",
    howItWorks: "Players are paired off. The group votes on each pair. The loser of each duel is out.",
    header: "Duels: Pick a Side",
    cta: "Who should survive?",
    oneLiner: "Head-to-head \xB7 Group picks survivors",
    moodSubtitle: "Two enter \u2014 one stays",
    revealLabel: "Lost",
    confirmTemplate: "Save {name}?",
    actionVerb: "voted for",
    eliminatedSubtitle: "Lost the duel",
    noEliminationCopy: "Both duelists survive",
    mechanismTone: "punitive",
    minPlayers: 4,
    selectableInLobby: false
  },
  FINALS: {
    name: "Finals",
    description: "The jury of eliminated players crowns the winner.",
    howItWorks: "Eliminated players are the jury \u2014 they crown one survivor. Most votes wins the game.",
    header: "Finals: Crown the Winner",
    cta: "Who played the best game?",
    oneLiner: "Jury votes \xB7 Most votes wins",
    moodSubtitle: "The ghosts crown the winner",
    revealLabel: "Crowned",
    confirmTemplate: "Crown {name}?",
    actionVerb: "crowned",
    eliminatedSubtitle: "",
    noEliminationCopy: "",
    mechanismTone: "celebratory",
    minPlayers: 2,
    selectableInLobby: false,
    winnerSubtitle: "Top of the pecking order"
  }
};

// src/dilemma-type-info.ts
var DILEMMA_TYPE_INFO = {
  SILVER_GAMBIT: {
    name: "Silver Gambit",
    description: "All-or-nothing donation gamble",
    howItWorks: "If all players chose to donate five silver, one player will get 15 silver back.",
    header: "Silver Gambit: Donate or Keep",
    cta: "Do you trust everyone to donate?",
    actionVerb: "decided"
  },
  SPOTLIGHT: {
    name: "Spotlight",
    description: "Blind unanimous pick",
    howItWorks: "If you all agree on one person (in a blind submission) that person will get 20 silver.",
    header: "Spotlight: Pick a Player",
    cta: "Who does everyone agree on?",
    actionVerb: "decided"
  },
  GIFT_OR_GRIEF: {
    name: "Gift or Grief",
    description: "Nominate for fortune or ruin",
    howItWorks: "Write down any person but you. That person will get +10 silver or -10 at the end of the group chat.",
    header: "Gift or Grief: Name a Player",
    cta: "Who are you nominating?",
    actionVerb: "decided"
  }
};

// src/game-type-info.ts
var GAME_TYPE_INFO = {
  TRIVIA: { name: "Trivia", description: "Answer questions fast to earn silver", category: "knowledge" },
  REALTIME_TRIVIA: { name: "Live Trivia", description: "Speed-based trivia with live scoring", category: "knowledge" },
  GAP_RUN: { name: "Gap Run", description: "Navigate through the gaps to survive", category: "arcade" },
  GRID_PUSH: { name: "Grid Push", description: "Push blocks on the grid strategically", category: "arcade" },
  SEQUENCE: { name: "Sequence", description: "Repeat the pattern from memory", category: "arcade" },
  REACTION_TIME: { name: "Reaction Time", description: "Test your reflexes \u2014 fastest wins", category: "arcade" },
  COLOR_MATCH: { name: "Color Match", description: "Match the right colors fast", category: "arcade" },
  STACKER: { name: "Stacker", description: "Stack blocks with precision", category: "arcade" },
  QUICK_MATH: { name: "Quick Math", description: "Solve equations under pressure", category: "arcade" },
  SIMON_SAYS: { name: "Simon Says", description: "Follow the pattern, don't miss a beat", category: "arcade" },
  AIM_TRAINER: { name: "Aim Trainer", description: "Hit targets accurately and fast", category: "arcade" },
  TOUCH_SCREEN: { name: "Touch Screen", description: "Tap your way to victory", category: "social" },
  BET_BET_BET: { name: "Bet Bet Bet", description: "Place your bets wisely", category: "social" },
  BLIND_AUCTION: { name: "Blind Auction", description: "Bid without seeing others' bets", category: "social" },
  KINGS_RANSOM: { name: "King's Ransom", description: "A royal risk-reward gamble", category: "social" },
  THE_SPLIT: { name: "The Split", description: "Split or steal \u2014 trust is everything", category: "social" },
  SHOCKWAVE: { name: "Shockwave", description: "Dodge contracting rings in a neon arena", category: "arcade" },
  ORBIT: { name: "Orbit", description: "Slingshot between gravity wells \u2014 timing is everything", category: "arcade" },
  BEAT_DROP: { name: "Beat Drop", description: "Hit notes on the beat, build combos, don't miss", category: "arcade" },
  INFLATE: { name: "Inflate", description: "Push your luck, don't pop", category: "arcade" },
  SNAKE: { name: "Snake", description: "Eat pellets, grow your snake, don't crash", category: "arcade" },
  FLAPPY: { name: "Flappy", description: "Tap to flap, thread the gaps, grab coins", category: "arcade" },
  COLOR_SORT: { name: "Color Sort", description: "Sort balls into matching tubes", category: "arcade" },
  BLINK: { name: "Blink", description: "Tap on black, freeze on white \u2014 the flash is a trap", category: "arcade" },
  RECALL: { name: "Recall", description: "Memorize the grid. Tap the numbers in order from memory", category: "arcade" }
};

// src/cartridge-info.ts
var CARTRIDGE_INFO = {
  // --- Voting (9) ---
  EXECUTIONER: {
    kind: "voting",
    displayName: "Executioner Vote",
    tagline: "One vote. One execution. No mercy.",
    description: "Everyone votes in secret. The player with the most votes is eliminated on the spot. Ties go to the executioner \u2014 one player chosen at random.",
    mechanics: [
      "Every alive player casts one vote",
      "Highest vote count is eliminated",
      "Ties resolved by the random executioner",
      "Votes stay secret until the reveal"
    ]
  },
  MAJORITY: {
    kind: "voting",
    displayName: "Majority Vote",
    tagline: "Over half, or nobody goes home.",
    description: "You need a clear majority to eliminate. Fall short and the day ends with everyone still in the game \u2014 for now.",
    mechanics: [
      "Every alive player casts one vote",
      "A strict majority is required to eliminate",
      "No majority \u2192 no elimination today",
      "Alliances either hold or crack in public"
    ]
  },
  BUBBLE: {
    kind: "voting",
    displayName: "Bubble Vote",
    tagline: "Save someone. Doom someone else.",
    description: "Each player names one person to protect. The player with the fewest shields is the bubble \u2014 and bubbles pop.",
    mechanics: [
      "Vote for the player you want to SAVE",
      "Fewest save-votes is eliminated",
      "You cannot save yourself",
      "Shields stay secret until the reveal"
    ]
  },
  SECOND_TO_LAST: {
    kind: "voting",
    displayName: "Second-to-Last",
    tagline: "Not the top. Not the bottom. The trap.",
    description: "The player with the second-highest vote count is eliminated. Leading means nothing \u2014 hiding in plain sight is everything.",
    mechanics: [
      "Every alive player casts one vote",
      "Second-highest vote count is eliminated",
      "Top vote-getter survives",
      "Ties at second resolved by random pick"
    ]
  },
  PODIUM_SACRIFICE: {
    kind: "voting",
    displayName: "Podium Sacrifice",
    tagline: "Rank three. The top one burns.",
    description: "Everyone picks their top three threats in order. Points are assigned \u2014 and the player with the most points leaves the game.",
    mechanics: [
      "Pick 1st, 2nd, and 3rd place",
      "1st = 3 points, 2nd = 2, 3rd = 1",
      "Highest total is eliminated",
      "Partial rankings still count"
    ]
  },
  SHIELD: {
    kind: "voting",
    displayName: "Shield Vote",
    tagline: "Elimination is normal. Immunity is not.",
    description: "The majority vote eliminates someone \u2014 unless the target has a shield. Shields are earned, stolen, or bought. Play accordingly.",
    mechanics: [
      "Every alive player casts one vote",
      "Highest vote count is the target",
      "If the target holds a shield, they survive",
      "Shields burn after one use"
    ]
  },
  TRUST_PAIRS: {
    kind: "voting",
    displayName: "Trust Pairs",
    tagline: "Pick your partner. Hope they pick you.",
    description: "Everyone names the player they trust most. Mutual picks form a pair and survive. Lone picks are sitting ducks.",
    mechanics: [
      "Name one player you trust",
      "Mutual picks form safe pairs",
      "Unpaired players face elimination",
      "Highest vote among the unpaired goes home"
    ]
  },
  DUELS: {
    kind: "voting",
    displayName: "Duels",
    tagline: "Pick a fight. Win or leave.",
    description: "Challenge another player head-to-head. The room picks a winner for each duel. Losers are eliminated; winners walk on.",
    mechanics: [
      "Challenge any alive player",
      "Audience votes the winner",
      "Losers are eliminated",
      "Unchallenged players are safe this round"
    ]
  },
  FINALS: {
    kind: "voting",
    displayName: "Finals",
    tagline: "Last vote. One winner.",
    description: "The remaining players face a final jury vote. The player with the most jury votes wins the game. No elimination \u2014 a coronation.",
    mechanics: [
      "Eliminated players form the jury",
      "Each juror casts one vote for the winner",
      "Highest vote count wins",
      "Ties resolved by most silver at showdown"
    ]
  },
  // --- Games (25) ---
  TRIVIA: {
    kind: "game",
    displayName: "Trivia Blitz",
    tagline: "Fastest right answer wins.",
    description: "Rapid-fire questions pulled from the cast's pre-game interviews. Speed matters as much as accuracy. Silver to the top three.",
    mechanics: [
      "Five rounds, one question each",
      "Pick from four answers",
      "Fastest correct answer scores most",
      "Top 3 split the silver pot"
    ]
  },
  REALTIME_TRIVIA: {
    kind: "game",
    displayName: "Live Trivia",
    tagline: "Everyone answers at once. Fastest gets paid.",
    description: "Live simultaneous rounds \u2014 you see the scoreboard shift in real time. Pressure cooks the lead.",
    mechanics: [
      "All players answer the same question at once",
      "Faster correct answers score higher",
      "Wrong answers cost you time on the next one",
      "Running leaderboard displays between rounds"
    ]
  },
  GAP_RUN: {
    kind: "game",
    displayName: "Gap Run",
    tagline: "Find the opening. Keep moving.",
    description: "Navigate a scrolling field of obstacles. Time the gaps \u2014 brush a wall and you're out.",
    mechanics: [
      "Swipe or drag to steer",
      "Pass through gaps to score",
      "Contact with obstacles ends your run",
      "Longest distance wins the round"
    ]
  },
  GRID_PUSH: {
    kind: "game",
    displayName: "Grid Push",
    tagline: "Every push changes everything.",
    description: "Shove blocks on a shared grid. Line up your color to score. Disrupt rivals when you can \u2014 subtly is optional.",
    mechanics: [
      "Tap a direction to push a row or column",
      "Align 3+ of your color to score",
      "Opponent moves shift the board too",
      "Highest score at timeout wins"
    ]
  },
  SEQUENCE: {
    kind: "game",
    displayName: "Sequence",
    tagline: "Watch. Remember. Repeat.",
    description: "A pattern flashes. Repeat it back exactly. Each round adds one more step \u2014 and one more chance to crack.",
    mechanics: [
      "Watch the pattern light up",
      "Tap the tiles in the same order",
      "Each correct round adds one step",
      "One mistake and you're out"
    ]
  },
  REACTION_TIME: {
    kind: "game",
    displayName: "Reaction Time",
    tagline: "Tap the instant the light goes green.",
    description: "Pure reflex. Wait for the signal \u2014 tap early and you're disqualified, tap late and you lose the round.",
    mechanics: [
      "Watch the light",
      "Tap the instant it turns green",
      "Early taps disqualify the round",
      "Fastest clean reaction wins"
    ]
  },
  COLOR_MATCH: {
    kind: "game",
    displayName: "Color Match",
    tagline: "Trust your eyes. Not the words.",
    description: "The word says one color. The ink is a different color. Tap the ink color, not the word. Fast.",
    mechanics: [
      "Read the ink, not the word",
      "Tap the matching color button",
      "Correct taps score",
      "Wrong taps cost time"
    ]
  },
  STACKER: {
    kind: "game",
    displayName: "Stacker",
    tagline: "Timing is everything. Height is a bonus.",
    description: "Stack blocks precisely on top of each other. Every miss trims your base. Keep it tight or watch the tower crumble.",
    mechanics: [
      "Tap to drop the moving block",
      "Misaligned blocks lose their overhang",
      "Tower collapses when the base runs out",
      "Highest stack wins"
    ]
  },
  QUICK_MATH: {
    kind: "game",
    displayName: "Quick Math",
    tagline: "Numbers. Pressure. Go.",
    description: "Simple equations at speed. Tap the right answer before the timer catches you.",
    mechanics: [
      "Read the equation",
      "Tap the correct answer from four options",
      "Correct answers extend the round",
      "Most solved at timeout wins"
    ]
  },
  SIMON_SAYS: {
    kind: "game",
    displayName: "Simon Says",
    tagline: "Follow the pattern. Don't miss a beat.",
    description: "A classic memory game with a reality-TV twist \u2014 the patterns tie to cast events. Miss a step and you're done.",
    mechanics: [
      "Watch the pattern",
      "Repeat it in order",
      "Each round grows by one",
      "Perfect runs compound silver"
    ]
  },
  AIM_TRAINER: {
    kind: "game",
    displayName: "Aim Trainer",
    tagline: "Targets pop. You pop them faster.",
    description: "Tap as many targets as you can before the timer runs out. Small targets, big targets, moving targets \u2014 they all pay differently.",
    mechanics: [
      "Tap targets to score",
      "Smaller targets = more points",
      "Missed taps waste time",
      "Highest total at timeout wins"
    ]
  },
  BET_BET_BET: {
    kind: "game",
    displayName: "Bet Bet Bet",
    tagline: "Three bets. One truth. Read the room.",
    description: "Place bets on cast claims \u2014 which are true, which are bluffs. Confidence pays off. Overconfidence bankrupts.",
    mechanics: [
      "Read each claim",
      "Bet silver on TRUE or BLUFF",
      "Higher bets = higher rewards",
      "Bad calls burn the pot"
    ]
  },
  BLIND_AUCTION: {
    kind: "game",
    displayName: "Blind Auction",
    tagline: "Bid without seeing. Pay without regret.",
    description: "Mystery prizes up for bid. You write your number in secret. Highest bidder wins \u2014 and pays exactly what they wrote.",
    mechanics: [
      "Prize hints are revealed",
      "Submit your secret bid",
      "Highest bid wins and pays",
      "Ties resolved by earliest bid"
    ]
  },
  KINGS_RANSOM: {
    kind: "game",
    displayName: "King's Ransom",
    tagline: "Hold the throne. Hope nobody notices.",
    description: "One player holds the ransom. Others pool silver to steal it. Every round the temptation grows \u2014 and the risk.",
    mechanics: [
      "One player starts as king",
      "Challengers contribute silver to a pot",
      "If the pot exceeds the throne's value, the king is toppled",
      "Survive three rounds to keep it all"
    ]
  },
  THE_SPLIT: {
    kind: "game",
    displayName: "The Split",
    tagline: "Split or steal. Trust is everything.",
    description: "Two players. One pot. Both pick SPLIT and share. One picks STEAL and takes it all. Both pick STEAL and nobody wins.",
    mechanics: [
      "Two players are paired",
      "Each chooses SPLIT or STEAL",
      "SPLIT + SPLIT \u2192 share the pot",
      "STEAL + SPLIT \u2192 stealer takes all",
      "STEAL + STEAL \u2192 pot vanishes"
    ]
  },
  TOUCH_SCREEN: {
    kind: "game",
    displayName: "Touch Screen",
    tagline: "Keep a finger on the glass. Don't let go.",
    description: "Last finger touching wins. Simple rules \u2014 until the distractions start. Pop-ups, wiggles, fake prompts. Hold fast.",
    mechanics: [
      "Press and hold the screen",
      "Release = you're out",
      "Distractions will test your grip",
      "Last player holding wins"
    ]
  },
  SHOCKWAVE: {
    kind: "game",
    displayName: "Shockwave",
    tagline: "Dodge contracting rings in a neon arena.",
    description: "The arena shrinks. You don't. Weave between closing rings and outlast everyone else inside the shrinking circle.",
    mechanics: [
      "Drag to move your avatar",
      "Avoid the contracting rings",
      "Ring contact ends your run",
      "Last survivor wins the round"
    ]
  },
  ORBIT: {
    kind: "game",
    displayName: "Orbit",
    tagline: "Slingshot between gravity wells.",
    description: "Use planetary pull to sling yourself between targets. Miss the pull and you drift into the void. Nail the curve and silver rains.",
    mechanics: [
      "Tap to release from the current orbit",
      "Gravity bends your trajectory",
      "Collect checkpoints in order",
      "Fastest clean orbit wins"
    ]
  },
  BEAT_DROP: {
    kind: "game",
    displayName: "Beat Drop",
    tagline: "Tap on the beat. Miss and you're out.",
    description: "A rhythm game tied to the music. Hit the beats as they come \u2014 early or late, you lose the combo. Perfect runs pay the most.",
    mechanics: [
      "Tap on the beat",
      "Perfect, Good, or Miss",
      "Misses break your combo",
      "Highest combo score wins"
    ]
  },
  INFLATE: {
    kind: "game",
    displayName: "Inflate",
    tagline: "Pump it up. Don't pop.",
    description: "Press and hold to inflate. Release to cash in. Every extra moment pays more \u2014 but push one pump too far and it bursts.",
    mechanics: [
      "Press and hold to inflate",
      "Release to bank your score",
      "Longer holds pay exponentially",
      "Burst = you lose the round"
    ]
  },
  SNAKE: {
    kind: "game",
    displayName: "Snake",
    tagline: "Grow longer. Don't eat yourself.",
    description: "The classic. Eat the dots. Every bite makes you longer \u2014 and harder to maneuver. Run into your own tail and it's over.",
    mechanics: [
      "Swipe to change direction",
      "Eat dots to grow",
      "Don't hit walls or yourself",
      "Longest snake wins"
    ]
  },
  FLAPPY: {
    kind: "game",
    displayName: "Flappy",
    tagline: "Tap to fly. Gravity is unforgiving.",
    description: "Tap to keep your bird aloft. Thread the gaps. Every pipe you clear is one more than the players who folded.",
    mechanics: [
      "Tap to flap upward",
      "Gravity pulls you down",
      "Pass between pipes to score",
      "Furthest distance wins"
    ]
  },
  COLOR_SORT: {
    kind: "game",
    displayName: "Color Sort",
    tagline: "Pour the colors into neat stacks.",
    description: "Pour liquids between vials until each vial holds one color. Sounds simple. Spills are permanent.",
    mechanics: [
      "Tap a vial, then tap a target",
      "Pours only land if colors match",
      "Sort every vial to solve",
      "Fewest moves wins"
    ]
  },
  BLINK: {
    kind: "game",
    displayName: "Blink",
    tagline: "Don't blink. Or do. Hard to say.",
    description: "Focus on the screen. Something flashes \u2014 a word, a face, a number. Did you see it? Tap what you saw before it's gone for good.",
    mechanics: [
      "Watch the screen closely",
      "Brief flashes reveal targets",
      "Tap what you saw from the options",
      "Accuracy and speed both score"
    ]
  },
  RECALL: {
    kind: "game",
    displayName: "Recall",
    tagline: "Memorize everything. Then prove it.",
    description: "A scene fills the screen \u2014 faces, items, positions. It vanishes. Now answer questions about what you saw.",
    mechanics: [
      "Study the scene while it's visible",
      "Scene disappears after a beat",
      "Answer questions from memory",
      "Top scorers split the pot"
    ]
  },
  // --- Prompts (6) ---
  PLAYER_PICK: {
    kind: "prompt",
    displayName: "Player Pick",
    tagline: "Name one. Everyone sees.",
    description: "Answer the question by naming a player. Your pick is public. Agreements form; grudges remember.",
    mechanics: [
      "Read the prompt",
      "Pick one player from the cast",
      "All picks are revealed together",
      "Most-picked player gets a spotlight moment"
    ]
  },
  PREDICTION: {
    kind: "prompt",
    displayName: "Prediction",
    tagline: "Call the future. Cash in if you're right.",
    description: "Bet on what happens next in the game. Correct predictions pay silver when the moment arrives.",
    mechanics: [
      "Read the prediction prompt",
      "Submit your answer",
      "Answers stay hidden until payoff",
      "Correct predictions earn silver later"
    ]
  },
  WOULD_YOU_RATHER: {
    kind: "prompt",
    displayName: "Would You Rather",
    tagline: "Pick your poison.",
    description: "An impossible choice. Your answer is public. Disagreements start fights \u2014 which is the point.",
    mechanics: [
      "Read the two options",
      "Pick one (you cannot abstain)",
      "Answers revealed to everyone",
      "Discuss in chat \u2014 silver for the loudest takes"
    ]
  },
  HOT_TAKE: {
    kind: "prompt",
    displayName: "Hot Take",
    tagline: "Spicy opinions only.",
    description: "Write a one-liner opinion on the prompt. The cast votes on the spiciest take. Winner takes silver.",
    mechanics: [
      "Read the prompt",
      "Submit a one-line take",
      "Cast votes for the spiciest",
      "Top take wins the silver pot"
    ]
  },
  CONFESSION: {
    kind: "prompt",
    displayName: "Confession",
    tagline: "Say it anonymous. We'll all guess.",
    description: "Submit an anonymous confession in response to the prompt. Then the cast guesses who said what.",
    mechanics: [
      "Submit an anonymous answer",
      "All answers revealed without names",
      "Vote on who wrote each one",
      "Correct guesses and hidden authors both score"
    ]
  },
  GUESS_WHO: {
    kind: "prompt",
    displayName: "Guess Who",
    tagline: "Which cast member? Read the clues.",
    description: "Clues describe one player \u2014 from their bio, their history, their choices. Pick the match before time runs out.",
    mechanics: [
      "Read the clues as they appear",
      "Pick the cast member they describe",
      "Faster correct guesses score more",
      "Top guessers split the silver pot"
    ]
  },
  // --- Dilemmas (3) ---
  SILVER_GAMBIT: {
    kind: "dilemma",
    displayName: "Silver Gambit",
    tagline: "Keep it all or split it all.",
    description: "You and one other player are offered a pot of silver. Both cooperate and split it evenly. One defects and takes it all. Both defect and nobody wins.",
    mechanics: [
      "Two players paired in secret",
      "Each chooses: SPLIT or STEAL",
      "SPLIT + SPLIT \u2192 even share",
      "STEAL + SPLIT \u2192 stealer takes all",
      "STEAL + STEAL \u2192 pot vanishes"
    ]
  },
  SPOTLIGHT: {
    kind: "dilemma",
    displayName: "Spotlight",
    tagline: "Step into the light. Or push someone else.",
    description: "Nominate yourself for the spotlight \u2014 risk and reward scale with how many nominate. Push someone else to take the hit.",
    mechanics: [
      "Choose: step forward or push a rival",
      "Spotlight outcome depends on who chooses what",
      "Brave picks can win big or lose everything",
      "Push picks share the consequence"
    ]
  },
  GIFT_OR_GRIEF: {
    kind: "dilemma",
    displayName: "Gift or Grief",
    tagline: "Send a blessing. Or a curse.",
    description: "Secretly hand the target a boost \u2014 or a setback. They find out what you sent, but not necessarily who.",
    mechanics: [
      "Pick one target player",
      "Choose GIFT (silver to them) or GRIEF (silver from them)",
      "The target sees the outcome, not the sender",
      "Multiple senders compound the effect"
    ]
  }
};

// src/activity-type-info.ts
var ACTIVITY_TYPE_INFO = {
  PLAYER_PICK: { name: "Player Pick", description: "Pick a player for a scenario", promptText: "Pick your bestie" },
  PREDICTION: { name: "Prediction", description: "Predict what happens next", promptText: "Who do you think will be eliminated tonight?" },
  WOULD_YOU_RATHER: { name: "Would You Rather", description: "Choose between two options", promptText: "Would you rather...", options: { optionA: "Have immunity for one round", optionB: "Get 50 bonus silver" } },
  HOT_TAKE: { name: "Hot Take", description: "Share a controversial opinion", promptText: "Hot take" },
  CONFESSION: { name: "Confession", description: "Reveal something about yourself", promptText: "Confess something about your game strategy" },
  GUESS_WHO: { name: "Guess Who", description: "Figure out who said what", promptText: "What is your biggest fear in this game?" }
};

// src/action-info.ts
var ACTION_INFO = {
  OPEN_DMS: {
    name: "DMs Open",
    description: "Private messaging is now available.",
    howItWorks: "Send private messages to other players. Each message costs {silverCost} silver."
  },
  CLOSE_DMS: {
    name: "DMs Close",
    description: "Private messaging is now closed.",
    howItWorks: "Private messaging has closed for the day."
  },
  OPEN_GROUP_CHAT: {
    name: "Group Chat Opens",
    description: "The main group chat is open.",
    howItWorks: "The main group chat is open. Everyone can see these messages."
  },
  CLOSE_GROUP_CHAT: {
    name: "Group Chat Closes",
    description: "The group chat has closed.",
    howItWorks: "The group chat has closed for the day."
  },
  OPEN_VOTING: {
    name: "Voting Opens",
    description: "Cast your vote.",
    howItWorks: "Cast your vote. Details depend on the vote type."
  },
  CLOSE_VOTING: {
    name: "Voting Closes",
    description: "Voting has ended.",
    howItWorks: "The votes have been counted."
  },
  START_GAME: {
    name: "Mini-Game",
    description: "A mini-game is starting.",
    howItWorks: "Play the mini-game to earn silver. Silver breaks vote ties and unlocks perks."
  },
  END_GAME: {
    name: "Game Over",
    description: "The mini-game has ended.",
    howItWorks: "Check the results to see how you placed."
  },
  START_ACTIVITY: {
    name: "Activity",
    description: "An activity is starting.",
    howItWorks: "Answer the prompt to reveal your personality and earn silver."
  },
  END_ACTIVITY: {
    name: "Activity Ends",
    description: "The activity has ended.",
    howItWorks: "Check the results to see what everyone said."
  },
  START_DILEMMA: {
    name: "Daily Dilemma",
    description: "A social dilemma has been posed.",
    howItWorks: "Make your choice before time runs out. The outcome depends on everyone."
  },
  END_DILEMMA: {
    name: "Dilemma Ends",
    description: "The dilemma has been resolved.",
    howItWorks: "See how the group decided."
  },
  END_DAY: {
    name: "Day Ends",
    description: "The day is over.",
    howItWorks: "Review the day summary before the next day begins."
  }
};
function renderActionInfo(action, params) {
  const info = ACTION_INFO[action];
  if (!info) {
    return { name: action, description: "", howItWorks: "" };
  }
  const silverCost = params?.silverCost ?? Config.dm.silverCost;
  const resolve = (text) => text.replace(/\{silverCost\}/g, String(silverCost));
  return {
    name: resolve(info.name),
    description: resolve(info.description),
    howItWorks: resolve(info.howItWorks)
  };
}

// src/welcome-content.ts
var WELCOME_MESSAGES = [
  "Welcome, players. You have been selected.",
  "Each of you is wearing a mask. A persona. Nobody knows who you really are.",
  "Every day, you will chat, play games, and earn silver.",
  "Use your DMs wisely \u2014 alliances win games, but every message costs silver.",
  "At the end of each day, you will vote. Someone will be eliminated.",
  "The last player standing claims the gold prize pool.",
  "Keep your friends close, and your enemies closer."
];

// src/phase-info.ts
function buildPhaseInfo(phase, params) {
  const dayIndex = params?.dayIndex ?? 1;
  const aliveCount = params?.aliveCount ?? 0;
  const totalDays = params?.totalDays ?? "?";
  const voteType = params?.voteType;
  const voteInfo = voteType ? VOTE_TYPE_INFO[voteType] : null;
  switch (phase) {
    case DayPhases.MORNING: {
      const parts = [];
      if (voteInfo) parts.push(`Vote: ${voteInfo.name}`);
      if (params?.gameType && params.gameType !== "NONE") parts.push("Mini-game today");
      if (params?.activityType && params.activityType !== "NONE") parts.push("Activity today");
      return {
        title: `Day ${dayIndex}`,
        subtitle: `${aliveCount} players remaining \xB7 Day ${dayIndex} of ${totalDays}`,
        body: parts.length > 0 ? parts.join(" \xB7 ") : "Chat, strategize, and prepare for tonight\u2019s vote.",
        detail: voteInfo?.howItWorks,
        detailLabel: voteInfo ? `Tonight: ${voteInfo.name}` : void 0
      };
    }
    case DayPhases.SOCIAL:
      return {
        title: "Social Hour",
        subtitle: params?.dmsOpen ? "DMs are now open" : "Chat and strategize",
        body: params?.dmsOpen ? "Send private messages, form alliances, and gather information. Your DM slots are limited \u2014 choose wisely." : "Talk in the group chat, check the schedule, and prepare for what\u2019s ahead."
      };
    case DayPhases.GAME: {
      const gameType = params?.gameType;
      const gameInfo = gameType && gameType !== "NONE" ? GAME_TYPE_INFO[gameType] : null;
      return {
        title: gameInfo ? gameInfo.name : "Game Time",
        subtitle: "Earn silver to stay in the game",
        body: gameInfo ? gameInfo.description : "Play the mini-game to earn silver. Silver breaks vote ties and unlocks perks \u2014 every coin matters."
      };
    }
    case DayPhases.ACTIVITY: {
      const actType = params?.activityType;
      const actInfo = actType && actType !== "NONE" ? ACTIVITY_TYPE_INFO[actType] : null;
      return {
        title: actInfo ? actInfo.name : "Activity",
        subtitle: actInfo ? actInfo.description : "Express yourself",
        body: "Answer the prompt to earn silver. Other players will see your response."
      };
    }
    case DayPhases.VOTING:
      return {
        title: "Voting",
        subtitle: voteInfo ? voteInfo.name : "Cast your vote",
        body: voteInfo?.description ?? "Choose carefully \u2014 someone will be eliminated.",
        detail: voteInfo?.howItWorks,
        detailLabel: "How it works"
      };
    case DayPhases.ELIMINATION:
      return {
        title: "Elimination",
        subtitle: "The votes have been counted",
        body: "See who was eliminated and check the vote breakdown in your dashboard."
      };
    case DayPhases.FINALE:
    case DayPhases.GAME_OVER:
      return {
        title: "Finale",
        subtitle: "The game is over",
        body: "The winner has been decided. Check the dashboard for the full results."
      };
    default:
      return null;
  }
}

// src/gm-briefings.ts
function buildDayBriefingMessages(day, aliveCount) {
  const messages = [];
  const voteInfo = VOTE_TYPE_INFO[day.voteType];
  const parts = [
    `Day ${day.dayIndex} begins. ${aliveCount} players remain.`
  ];
  if (voteInfo) {
    parts.push(`Tonight's vote: ${voteInfo.name} \u2014 ${voteInfo.howItWorks}`);
  }
  if (day.gameType && day.gameType !== "NONE") {
    const gameInfo = GAME_TYPE_INFO[day.gameType];
    if (gameInfo) {
      parts.push(`Today's game: ${gameInfo.name} \u2014 ${gameInfo.description}`);
    }
  }
  if (day.activityType && day.activityType !== "NONE") {
    const actInfo = ACTIVITY_TYPE_INFO[day.activityType];
    if (actInfo) {
      parts.push(`Today's activity: ${actInfo.name} \u2014 ${actInfo.description}`);
    }
  }
  messages.push(parts.join("\n\n"));
  if (day.dilemmaType && day.dilemmaType !== "NONE") {
    const dilemmaInfo = DILEMMA_TYPE_INFO[day.dilemmaType];
    if (dilemmaInfo) {
      messages.push(`Today's dilemma: ${dilemmaInfo.name} \u2014 ${dilemmaInfo.howItWorks}`);
    }
  }
  return messages;
}

// src/channel-hints.ts
function getChannelHints(channelType, params) {
  const hints = [];
  const name = params?.targetName ?? "them";
  const caps = params?.capabilities ?? [];
  const phase = params?.phase;
  if (phase === DayPhases.VOTING) {
    hints.push("Quick, before votes close...");
  }
  if (phase === DayPhases.GAME) {
    hints.push("Talk strategy...");
  }
  switch (channelType) {
    case "DM":
      hints.push(
        `Whisper to ${name}...`,
        "Make a deal...",
        "Form an alliance..."
      );
      break;
    case "GROUP_DM":
      hints.push(
        "Message the group...",
        "Invite someone new...",
        "Strategize together..."
      );
      break;
    case "GAME_DM":
      hints.push(
        "Message your partner...",
        "Coordinate your move..."
      );
      break;
    case "MAIN":
    default:
      hints.push(
        "Plot your next move...",
        "Make your case...",
        "Address the house..."
      );
      break;
  }
  if (caps.includes("SILVER_TRANSFER")) {
    hints.push("Send silver using $ below");
  }
  if (caps.includes("INVITE_MEMBER")) {
    hints.push("Invite players into this chat");
  }
  return hints;
}

// src/dilemma-types.ts
import { z } from "zod";
var DilemmaTypeSchema = z.enum([
  "SILVER_GAMBIT",
  "SPOTLIGHT",
  "GIFT_OR_GRIEF"
]);

// src/prompt-pools/hot-take.ts
var HOT_TAKE_POOL = [
  { id: "cereal-before-milk", statement: "Cereal should go in the bowl before milk.", options: ["Cereal first", "Milk first", "Wet cereal is wrong", "I don't do cereal"] },
  { id: "last-page-first", statement: "Reading the last page first is a crime.", options: ["Major crime", "Totally fine", "Only for mysteries"] },
  { id: "k-reply", statement: 'Replying just "k" is basically hostile.', options: ["Always hostile", "Totally neutral", "Depends on who"] },
  { id: "ghosting-early", statement: "Ghosting is fine if you barely know someone.", options: ["Fine", "Never fine", "Online only"] },
  { id: "long-voice-memos", statement: "Voice memos over thirty seconds are a red flag.", options: ["Red flag", "Love them", "Depends on sender"] },
  { id: "crocs-shoes", statement: "Crocs count as real shoes in public.", options: ["Yes", "No", "Only in Croc Mode"] },
  { id: "phones-at-dinner", statement: "Phones don't belong at the dinner table.", options: ["Hard rule", "Harmless", "Only at family dinners"] },
  { id: "on-time-means-early", statement: 'Being "on time" means five minutes early.', options: ["Five early", "Exactly on time", "Five late counts"] },
  { id: "liking-own-posts", statement: "Liking your own posts is cringe.", options: ["Cringe", "Confident", "Only on stories"] },
  { id: "crying-at-school", statement: "Crying at school is always OK.", options: ["Always fine", "Never fine", "Only in private"] },
  { id: "small-talk", statement: "Small talk is a survival skill, not a waste.", options: ["Survival skill", "Waste of breath", "Depends on the room"] },
  { id: "cats-vs-dogs", statement: "Cats are better than dogs.", options: ["Cats", "Dogs"] },
  { id: "dogs-on-bed", statement: "Dogs on the bed is a dealbreaker.", options: ["Dealbreaker", "Hard yes", "Small dogs only"] },
  { id: "ban-homework", statement: "Homework should be banned entirely.", options: ["Ban it", "Keep it", "Only for younger grades"] },
  { id: "uniforms-vs-free", statement: "Uniforms are better than free dress.", options: ["Uniforms", "Free dress"] },
  { id: "group-projects", statement: "Group projects teach resentment, not teamwork.", options: ["Facts", "They're valuable", "Depends on the group"] },
  { id: "winter-vs-summer", statement: "Winter is better than summer.", options: ["Winter", "Summer"] },
  { id: "ocean-is-scary", statement: "The ocean is genuinely terrifying.", options: ["Terrifying", "Calming", "Only the deep stuff"] },
  { id: "leftovers-better", statement: "Leftovers taste better than the first night.", options: ["Always", "Never", "Only some foods"] },
  { id: "night-showers", statement: "Night showers beat morning showers.", options: ["Night", "Morning", "Both, always"] },
  { id: "chat-size-limit", statement: "Group chats over eight people are always chaos.", options: ["Nuke them", "Size doesn't matter", "Depends on the group"] },
  { id: "playback-speed", statement: "Watching a show on 1.5x speed ruins it.", options: ["Ruins it", "More efficient", "Only on rewatch", "Depends on show"] },
  { id: "spoilers-fine", statement: "Spoilers genuinely don't ruin anything.", options: ["Fine", "They ruin it", "Depends on the story"] },
  { id: "theater-phones", statement: "Phones in a movie theater should be illegal.", options: ["Enforce it", "Live and let live", "Only during the movie"] },
  { id: "early-vs-late", statement: "Being really early is worse than being slightly late.", options: ["Worse", "Always better", "Depends on the event"] },
  { id: "socks-and-sandals", statement: "Socks with sandals deserves respect.", options: ["Respect the move", "Never OK", "Only in winter"] },
  { id: "birthdays-after-21", statement: "Birthdays stop being a big deal after twenty-one.", options: ["Agreed", "Every one counts", "Only round numbers"] },
  { id: "solo-restaurant", statement: "Eating alone at a restaurant is underrated.", options: ["Underrated", "Depressing", "Only at the bar"] },
  { id: "pen-vs-pencil", statement: "Writing in pen is better than pencil.", options: ["Pen", "Pencil", "Depends on the task"] },
  { id: "text-vs-call", statement: "A short call beats a long text thread.", options: ["Agreed", "Hard disagree", "Depends on the topic"] }
];
function pickHotTakeQuestion(usedIds) {
  const used = new Set(usedIds);
  const available = HOT_TAKE_POOL.filter((q) => !used.has(q.id));
  const pool = available.length > 0 ? available : HOT_TAKE_POOL;
  return pool[Math.floor(Math.random() * pool.length)];
}

// src/index.ts
var GameStatus = /* @__PURE__ */ ((GameStatus2) => {
  GameStatus2["OPEN"] = "OPEN";
  GameStatus2["FULL"] = "FULL";
  GameStatus2["IN_PROGRESS"] = "IN_PROGRESS";
  GameStatus2["COMPLETED"] = "COMPLETED";
  return GameStatus2;
})(GameStatus || {});
var GamePhase = /* @__PURE__ */ ((GamePhase2) => {
  GamePhase2["LOBBY"] = "LOBBY";
  GamePhase2["DAY_LOOP"] = "DAY_LOOP";
  GamePhase2["PAUSED"] = "PAUSED";
  return GamePhase2;
})(GamePhase || {});
var PlayerSchema = z2.object({
  id: z2.string(),
  email: z2.string().email(),
  name: z2.string(),
  personaId: z2.string().optional(),
  bio: z2.string().optional(),
  isHost: z2.boolean().default(false),
  status: z2.enum(["PENDING", "READY"])
});
var LobbyConfigSchema = z2.object({
  theme: z2.string().optional(),
  gameMode: z2.string().default("PECKING_ORDER")
});
var LobbySchema = z2.object({
  id: z2.string(),
  hostEmail: z2.string().email(),
  status: z2.nativeEnum(GameStatus),
  players: z2.array(PlayerSchema),
  config: LobbyConfigSchema
});
var TimelineEventSchema = z2.object({
  time: z2.string(),
  // "09:00" or ISO string
  action: z2.enum(["START_CARTRIDGE", "INJECT_PROMPT", "START_ACTIVITY", "END_ACTIVITY", "OPEN_VOTING", "CLOSE_VOTING", "ELIMINATE", "OPEN_DMS", "CLOSE_DMS", "OPEN_GROUP_CHAT", "CLOSE_GROUP_CHAT", "START_GAME", "END_GAME", "START_DILEMMA", "END_DILEMMA", "START_CONFESSION_CHAT", "END_CONFESSION_CHAT", "END_DAY"]),
  payload: z2.any().optional()
});
var VoteTypeSchema = z2.enum([
  "EXECUTIONER",
  "MAJORITY",
  "BUBBLE",
  "SECOND_TO_LAST",
  "PODIUM_SACRIFICE",
  "SHIELD",
  "TRUST_PAIRS",
  "DUELS",
  "FINALS"
]);
var GameTypeSchema = z2.enum([
  "TRIVIA",
  "REALTIME_TRIVIA",
  "GAP_RUN",
  "GRID_PUSH",
  "SEQUENCE",
  "REACTION_TIME",
  "COLOR_MATCH",
  "STACKER",
  "QUICK_MATH",
  "SIMON_SAYS",
  "AIM_TRAINER",
  "BET_BET_BET",
  "BLIND_AUCTION",
  "KINGS_RANSOM",
  "THE_SPLIT",
  "TOUCH_SCREEN",
  "SHOCKWAVE",
  "ORBIT",
  "BEAT_DROP",
  "INFLATE",
  "SNAKE",
  "FLAPPY",
  "COLOR_SORT",
  "BLINK",
  "RECALL",
  "NONE"
]);
var PromptTypeSchema = z2.enum(["PLAYER_PICK", "PREDICTION", "WOULD_YOU_RATHER", "HOT_TAKE", "CONFESSION", "GUESS_WHO"]);
var DailyManifestSchema = z2.object({
  dayIndex: z2.number(),
  theme: z2.string(),
  voteType: VoteTypeSchema,
  gameType: GameTypeSchema.default("NONE"),
  gameMode: z2.enum(["SOLO", "LIVE"]).optional(),
  activityType: PromptTypeSchema.or(z2.literal("NONE")).optional(),
  dilemmaType: DilemmaTypeSchema.or(z2.literal("NONE")).optional(),
  timeline: z2.array(TimelineEventSchema),
  nextDayStart: z2.string().optional(),
  // ISO 8601 — when the following day begins (undefined on last day)
  // Optional social parameters — director-resolved or lobby-configured
  dmCharsPerPlayer: z2.number().optional(),
  dmPartnersPerPlayer: z2.number().optional(),
  requireDmInvite: z2.boolean().optional(),
  dmSlotsPerPlayer: z2.number().optional(),
  dmCost: z2.number().optional(),
  disableNudgeThrottle: z2.boolean().optional()
});
var PushTriggerSchema = z2.enum([
  // Fact-driven — targeted
  "DM_SENT",
  // DM recipient
  "MENTION",
  // @mention in main chat (targeted to mentioned player)
  "REPLY",
  // reply to your main-chat message (targeted to original author)
  "WHISPER",
  // whisper recipient
  "NUDGE",
  // nudge recipient
  "SILVER_RECEIVED",
  // silver recipient
  // Fact-driven — broadcast
  "ELIMINATION",
  "WINNER_DECLARED",
  "GROUP_CHAT_MSG",
  // main-channel message (suppressed for mentioned / reply-to author)
  // Phase-transition
  "DAY_START",
  "ACTIVITY",
  "VOTING",
  "NIGHT_SUMMARY",
  "DAILY_GAME",
  // Gate events
  "OPEN_DMS",
  "CLOSE_DMS",
  "OPEN_GROUP_CHAT",
  "CLOSE_GROUP_CHAT",
  // Cartridge lifecycle
  "START_GAME",
  "END_GAME",
  "START_ACTIVITY",
  "END_ACTIVITY",
  "DILEMMA",
  "END_DILEMMA",
  // Confession phase
  "CONFESSION_OPEN"
]);
var PushConfigSchema = z2.record(PushTriggerSchema, z2.boolean()).default({});
var DEFAULT_PUSH_CONFIG = {
  DM_SENT: true,
  MENTION: true,
  REPLY: true,
  WHISPER: true,
  NUDGE: true,
  SILVER_RECEIVED: true,
  ELIMINATION: true,
  WINNER_DECLARED: true,
  GROUP_CHAT_MSG: true,
  DAY_START: true,
  ACTIVITY: true,
  VOTING: true,
  NIGHT_SUMMARY: true,
  DAILY_GAME: true,
  OPEN_DMS: true,
  CLOSE_DMS: true,
  OPEN_GROUP_CHAT: true,
  CLOSE_GROUP_CHAT: true,
  START_GAME: true,
  END_GAME: true,
  START_ACTIVITY: true,
  END_ACTIVITY: true,
  DILEMMA: true,
  END_DILEMMA: true,
  CONFESSION_OPEN: true
};
var SchedulingStrategySchema = z2.enum(["ADMIN", "PRE_SCHEDULED"]);
function resolveScheduling(manifest) {
  if (!manifest) return "PRE_SCHEDULED";
  if (manifest.scheduling === "ADMIN" || manifest.scheduling === "PRE_SCHEDULED") return manifest.scheduling;
  if (manifest.gameMode === "DEBUG_PECKING_ORDER") return "ADMIN";
  return "PRE_SCHEDULED";
}
var SchedulePresetSchema = z2.enum(["DEFAULT", "COMPACT", "SPEED_RUN", "SMOKE_TEST", "PLAYTEST", "PLAYTEST_SHORT", "PLAYTEST_NEXT"]);
var ScalingModeSchema = z2.enum(["FIXED", "PER_ACTIVE_PLAYER", "DIMINISHING"]);
var PeckingOrderVotingRulesSchema = z2.object({
  mode: z2.enum(["SEQUENCE", "POOL"]).optional(),
  sequence: z2.array(VoteTypeSchema).optional(),
  pool: z2.array(VoteTypeSchema).optional(),
  allowed: z2.array(VoteTypeSchema).optional(),
  constraints: z2.array(z2.object({
    voteType: VoteTypeSchema,
    minPlayers: z2.number()
  })).optional()
});
var PeckingOrderGameRulesSchema = z2.object({
  mode: z2.enum(["SEQUENCE", "POOL", "NONE"]).optional(),
  sequence: z2.array(GameTypeSchema).optional(),
  pool: z2.array(GameTypeSchema).optional(),
  allowed: z2.array(GameTypeSchema).optional(),
  avoidRepeat: z2.boolean().default(true)
});
var PeckingOrderActivityRulesSchema = z2.object({
  mode: z2.enum(["SEQUENCE", "POOL", "NONE"]).optional(),
  sequence: z2.array(PromptTypeSchema).optional(),
  pool: z2.array(PromptTypeSchema).optional(),
  allowed: z2.array(PromptTypeSchema).optional(),
  avoidRepeat: z2.boolean().default(true)
});
var PeckingOrderSocialRulesSchema = z2.object({
  dmChars: z2.object({ mode: ScalingModeSchema, base: z2.number(), floor: z2.number().optional() }),
  dmPartners: z2.object({ mode: ScalingModeSchema, base: z2.number(), floor: z2.number().optional() }),
  dmCost: z2.number(),
  groupDmEnabled: z2.boolean(),
  requireDmInvite: z2.boolean().default(false),
  dmSlotsPerPlayer: z2.number().min(1).max(20).default(5),
  /** When true, removes the per-(sender,target,day) nudge rate-limit. Default false (existing behavior). */
  disableNudgeThrottle: z2.boolean().optional()
});
var PeckingOrderInactivityRulesSchema = z2.object({
  enabled: z2.boolean(),
  thresholdDays: z2.number(),
  socketInactivityHours: z2.number().optional(),
  action: z2.enum(["ELIMINATE", "NUDGE_THEN_ELIMINATE"]),
  nudgeDays: z2.number().optional()
});
var PeckingOrderDayCountRulesSchema = z2.object({
  mode: z2.enum(["ACTIVE_PLAYERS_MINUS_ONE", "FIXED"]),
  fixedCount: z2.number().optional(),
  maxDays: z2.number().optional()
});
var PeckingOrderDilemmaRulesSchema = z2.object({
  mode: z2.enum(["SEQUENCE", "POOL", "NONE"]),
  sequence: z2.array(DilemmaTypeSchema).optional(),
  pool: z2.array(DilemmaTypeSchema).optional(),
  allowed: z2.array(DilemmaTypeSchema).optional(),
  avoidRepeat: z2.boolean().default(true)
});
var PeckingOrderRulesetSchema = z2.object({
  kind: z2.literal("PECKING_ORDER"),
  voting: PeckingOrderVotingRulesSchema,
  games: PeckingOrderGameRulesSchema,
  activities: PeckingOrderActivityRulesSchema,
  social: PeckingOrderSocialRulesSchema,
  inactivity: PeckingOrderInactivityRulesSchema,
  dayCount: PeckingOrderDayCountRulesSchema,
  dilemmas: PeckingOrderDilemmaRulesSchema.optional(),
  confessions: z2.object({
    enabled: z2.boolean()
  }).optional()
});
var GameRulesetSchema = z2.discriminatedUnion("kind", [
  PeckingOrderRulesetSchema
]);
var ManifestKindSchema = z2.enum(["STATIC", "DYNAMIC"]);
var legacyManifestFields = {
  id: z2.string().optional(),
  gameMode: z2.enum(["PECKING_ORDER", "CONFIGURABLE_CYCLE", "DEBUG_PECKING_ORDER"]).optional()
};
var StaticManifestSchema = z2.object({
  kind: z2.literal("STATIC"),
  scheduling: SchedulingStrategySchema.default("PRE_SCHEDULED"),
  days: z2.array(DailyManifestSchema),
  pushConfig: PushConfigSchema.optional(),
  ...legacyManifestFields
});
var DynamicManifestSchema = z2.object({
  kind: z2.literal("DYNAMIC"),
  scheduling: SchedulingStrategySchema.default("PRE_SCHEDULED"),
  startTime: z2.string(),
  // ISO 8601 — when Day 1 begins
  ruleset: GameRulesetSchema,
  schedulePreset: SchedulePresetSchema.default("DEFAULT"),
  maxPlayers: z2.number().optional(),
  minPlayers: z2.number().min(2).default(3),
  days: z2.array(DailyManifestSchema),
  pushConfig: PushConfigSchema.optional(),
  ...legacyManifestFields
});
var GameManifestSchema = z2.discriminatedUnion("kind", [
  StaticManifestSchema,
  DynamicManifestSchema
]);
function normalizeManifest(raw) {
  const manifest = raw?.kind === "STATIC" || raw?.kind === "DYNAMIC" ? raw : { kind: "STATIC", ...raw };
  if (Array.isArray(manifest.days)) {
    for (const day of manifest.days) {
      if (day.requireDmInvite === void 0) day.requireDmInvite = false;
      if (day.dmSlotsPerPlayer === void 0) day.dmSlotsPerPlayer = 5;
    }
  }
  if (manifest.kind === "DYNAMIC" && manifest.ruleset?.social) {
    if (manifest.ruleset.social.requireDmInvite === void 0) manifest.ruleset.social.requireDmInvite = false;
    if (manifest.ruleset.social.dmSlotsPerPlayer === void 0) manifest.ruleset.social.dmSlotsPerPlayer = 5;
  }
  return manifest;
}
var QaEntrySchema = z2.object({
  question: z2.string(),
  answer: z2.string(),
  narratorIntro: z2.string().optional()
});
var RosterPlayerSchema = z2.object({
  realUserId: z2.string(),
  // Opaque ID (Cookie/Hash), NOT email
  personaName: z2.string(),
  avatarUrl: z2.string(),
  bio: z2.string(),
  isAlive: z2.boolean(),
  isSpectator: z2.boolean(),
  silver: z2.number(),
  gold: z2.number(),
  destinyId: z2.string(),
  qaAnswers: z2.array(QaEntrySchema).optional()
});
var RosterSchema = z2.record(z2.string(), RosterPlayerSchema);
var InitPayloadSchema = z2.object({
  lobbyId: z2.string(),
  inviteCode: z2.string(),
  roster: RosterSchema,
  manifest: GameManifestSchema
});
var FactSchema = z2.object({
  type: z2.enum(["CHAT_MSG", "SILVER_TRANSFER", "VOTE_CAST", "ELIMINATION", "DM_SENT", "POWER_USED", "PERK_USED", "GAME_RESULT", "PLAYER_GAME_RESULT", "WINNER_DECLARED", "PROMPT_RESULT", "DM_INVITE_SENT", "DM_INVITE_ACCEPTED", "DM_INVITE_DECLINED", "DILEMMA_RESULT", "REACTION", "NUDGE", "WHISPER", "PREGAME_PLAYER_JOINED", "PREGAME_REVEAL_ANSWER"]),
  actorId: z2.string(),
  targetId: z2.string().optional(),
  payload: z2.any().optional(),
  // JSON details
  timestamp: z2.number()
});
var SocialPlayerSchema = z2.object({
  id: z2.string(),
  personaName: z2.string(),
  avatarUrl: z2.string(),
  bio: z2.string().optional(),
  status: z2.enum(["ALIVE", "ELIMINATED"]),
  silver: z2.number().int().default(0),
  gold: z2.number().int().default(0),
  realUserId: z2.string().optional(),
  qaAnswers: z2.array(QaEntrySchema).optional(),
  /** Day on which the player was eliminated. Populated at elimination time.
   *  Used by Pulse Phase 4 reveal replay keying (revealsSeen.elimination[dayIndex]). */
  eliminatedOnDay: z2.number().int().optional()
});
var ChatMessageSchema = z2.object({
  id: z2.string(),
  // UUID
  senderId: z2.string(),
  timestamp: z2.number(),
  content: z2.string().max(280),
  channelId: z2.string(),
  // Channel-based routing
  channel: z2.enum(["MAIN", "DM"]).optional(),
  // DEPRECATED — kept for migration
  targetId: z2.string().optional(),
  // DEPRECATED — kept for migration
  replyTo: z2.string().optional(),
  // messageId of the message being replied to
  whisperTarget: z2.string().optional(),
  // playerId — only sender+target see full content
  reactions: z2.record(z2.string(), z2.array(z2.string())).optional(),
  // emoji → [reactorPlayerIds]
  redacted: z2.boolean().optional()
  // true for whisper messages not visible to this player
});
var SocialEventSchema = z2.discriminatedUnion("type", [
  z2.object({
    type: z2.literal("SOCIAL.SEND_MSG"),
    content: z2.string(),
    targetId: z2.string().optional(),
    replyTo: z2.string().optional()
  }),
  z2.object({
    type: z2.literal("SOCIAL.SEND_SILVER"),
    amount: z2.number().positive(),
    targetId: z2.string()
  }),
  z2.object({
    type: z2.literal("SOCIAL.CREATE_CHANNEL"),
    memberIds: z2.array(z2.string()).min(2)
    // excludes sender (injected by L1)
  }),
  z2.object({
    type: z2.literal("SOCIAL.REACT"),
    messageId: z2.string(),
    emoji: z2.string()
  }),
  z2.object({
    type: z2.literal("SOCIAL.NUDGE"),
    targetId: z2.string()
  }),
  z2.object({
    type: z2.literal("SOCIAL.WHISPER"),
    targetId: z2.string(),
    text: z2.string().min(1).max(280)
  })
]);
var AdminEventSchema = z2.object({
  type: z2.literal("ADMIN.NEXT_STAGE")
});
var DM_MAX_PARTNERS_PER_DAY = Config.dm.maxPartnersPerDay;
var DM_MAX_CHARS_PER_DAY = Config.dm.maxCharsPerDay;
var DM_SILVER_COST = Config.dm.silverCost;
var DM_MAX_GROUPS_PER_DAY = Config.dm.maxGroupsPerDay;
var ChannelTypeSchema = z2.enum(["MAIN", "DM", "GROUP_DM", "GAME_DM", "CONFESSION"]);
function groupDmChannelId(memberIds) {
  return `gdm:${[...memberIds].sort().join(":")}`;
}
function gameDmChannelId(gameType, memberIds) {
  return `game-dm:${gameType}:${[...memberIds].sort().join(":")}`;
}
var PerkTypeSchema = z2.enum(["SPY_DMS", "EXTRA_DM_PARTNER", "EXTRA_DM_CHARS"]);
var PERK_COSTS = Config.perk.costs;
var GAME_MASTER_ID = "GAME_MASTER";
var GameMasterActionTypes = {
  ELIMINATE: "ELIMINATE"
};
var DayPhases = {
  PREGAME: "pregame",
  MORNING: "morning",
  SOCIAL: "social",
  GAME: "game",
  ACTIVITY: "activity",
  VOTING: "voting",
  ELIMINATION: "elimination",
  FINALE: "finale",
  GAME_OVER: "gameOver"
};
var GameTypes = GameTypeSchema.enum;
var VoteTypes = VoteTypeSchema.enum;
var PromptTypes = PromptTypeSchema.enum;
var ChannelTypes = ChannelTypeSchema.enum;
var PerkTypes = PerkTypeSchema.enum;
export {
  ACTION_INFO,
  ACTIVITY_TYPE_INFO,
  ALLOWED_CLIENT_EVENTS,
  ActivityEvents,
  AdminEventSchema,
  ArcadePhases,
  CARTRIDGE_INFO,
  ChannelTypeSchema,
  ChannelTypes,
  ChatMessageSchema,
  Config,
  DEFAULT_PUSH_CONFIG,
  DILEMMA_TYPE_INFO,
  DM_MAX_CHARS_PER_DAY,
  DM_MAX_GROUPS_PER_DAY,
  DM_MAX_PARTNERS_PER_DAY,
  DM_SILVER_COST,
  DailyManifestSchema,
  DayPhases,
  DilemmaEvents,
  DilemmaPhases,
  DilemmaTypeSchema,
  DynamicManifestSchema,
  ECONOMY_INFO,
  Events,
  FactSchema,
  FactTypes,
  GAME_MASTER_ID,
  GAME_TYPE_INFO,
  GameManifestSchema,
  GameMasterActionTypes,
  GamePhase,
  GameRulesetSchema,
  GameStatus,
  GameTypeSchema,
  GameTypes,
  HOT_TAKE_POOL,
  InitPayloadSchema,
  LiveGamePhases,
  LobbyConfigSchema,
  LobbySchema,
  PERK_COSTS,
  PeckingOrderActivityRulesSchema,
  PeckingOrderDayCountRulesSchema,
  PeckingOrderDilemmaRulesSchema,
  PeckingOrderGameRulesSchema,
  PeckingOrderInactivityRulesSchema,
  PeckingOrderRulesetSchema,
  PeckingOrderSocialRulesSchema,
  PeckingOrderVotingRulesSchema,
  PerkTypeSchema,
  PerkTypes,
  PlayerSchema,
  PlayerStatuses,
  PromptPhases,
  PromptTypeSchema,
  PromptTypes,
  PushConfigSchema,
  PushTriggerSchema,
  QaEntrySchema,
  RealtimeTriviaEvents,
  RealtimeTriviaPhases,
  RosterPlayerSchema,
  RosterSchema,
  ScalingModeSchema,
  SchedulePresetSchema,
  SchedulingStrategySchema,
  SocialEventSchema,
  SocialPlayerSchema,
  StaticManifestSchema,
  SyncDecisionPhases,
  TickerCategories,
  TimelineEventSchema,
  TouchScreenEvents,
  TriviaEvents,
  VOTE_TYPE_INFO,
  VoteEvents,
  VoteTypeSchema,
  VoteTypes,
  VotingPhases,
  WELCOME_MESSAGES,
  buildDayBriefingMessages,
  buildPhaseInfo,
  gameDmChannelId,
  generateCycleDefaults,
  getChannelHints,
  groupDmChannelId,
  isLiveGame,
  normalizeManifest,
  pickHotTakeQuestion,
  renderActionInfo,
  resolveScheduling
};
